// Public menu orchestrator — params, hooks, loading gate, and render delegation.
import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";
import { usePublicMenu, PUBLIC_API as API, PUBLIC_BACKEND_BASE as BACKEND_BASE } from "@/hooks/usePublicMenu";
import { usePublicMenuCart } from "@/hooks/usePublicMenuCart";
import { useCustomerSession } from "@/hooks/useCustomerSession";
import { useTableSession } from "@/hooks/useTableSession";
import { useBuffetRules } from "@/hooks/useBuffetRules";
import { useCheckout } from "@/hooks/useCheckout";
import { usePWAServiceWorker, usePWAManifest } from "@/components/public/PWAInstallPrompt";
import BrandedMenuLoader from "@/components/public/BrandedMenuLoader";
import { computeRestaurantStatus } from "@/utils/publicMenuHelpers";
import { findProductBySlug } from "@/utils/productSlug";
import { usePublicMenuEffects } from "@/hooks/usePublicMenuEffects";
import { PublicMenuLayout } from "@/components/public/PublicMenuLayout";
export { resolveMedia } from "@/utils/mediaUtils";

export default function PublicMenuPage({ overrideRestaurantId, overrideDeepLinkSlug, overrideLandingMode = false }) {
  const { restaurantId: paramId, productSlug: productDeepLinkSlug, offerSlug: legacyOfferSlug } = useParams();
  const restaurantId = overrideRestaurantId || paramId;
  const deepLinkSlug = overrideDeepLinkSlug || productDeepLinkSlug || legacyOfferSlug || null;
  const isProductLandingMode = overrideLandingMode || !!productDeepLinkSlug;
  const [landingModeActive, setLandingModeActive] = useState(isProductLandingMode);
  // [DEEP_LINK_DIAG] log; remove after confirming deep links work end-to-end
  if (deepLinkSlug) console.log('[DEEP_LINK_PAGE]', { deepLinkSlug, restaurantId, overrideDeepLinkSlug, isProductLandingMode });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tableParam = searchParams.get("table");
  const isTableOrder = !!tableParam;
  const paidOrderId = searchParams.get("order_confirmed");
  const isPaid = searchParams.get("paid") === "true";
  const cancelledOrderId = searchParams.get("order_cancelled");
  const waPayReturn = searchParams.get("wa_pay_return");
  const waPayCancel = searchParams.get("wa_pay_cancel");
  const influencerCode = searchParams.get("inf") || "";
  const cartToken = searchParams.get("cart") || "";
  const linkedCoupon = (searchParams.get("coupon") || "").trim().toUpperCase();
  const [linkedOfferPreview, setLinkedOfferPreview] = useState(null);

  // Attribution parameters (Meta Ads, utm tracking)
  const fbclid = searchParams.get("fbclid") || "";
  const fbc = searchParams.get("fbc") || "";
  const fbp = searchParams.get("fbp") || "";
  const utm_source = searchParams.get("utm_source") || "";
  const utm_medium = searchParams.get("utm_medium") || "";
  const utm_campaign = searchParams.get("utm_campaign") || "";
  const utm_content = searchParams.get("utm_content") || "";
  const utm_term = searchParams.get("utm_term") || "";

  const {
    restaurant, categories, products, combos, smartPricing, theme, loading,
    isSuspended, suspendedMessage, tableNumber, tableName, tableId,
    tablePayAtEnd, tableCurrentSessionId, tablePaymentEnabled, paymentAtTableEnabled,
    isRtl, fetchMenu,
  } = usePublicMenu(restaurantId, tableParam);

  const {
    cart, setCart, cartTotal, cartCount,
    addToCart, addToCartSilent, addSuggestionToCart, updateQuantity,
    suggestions, showSuggestions, setShowSuggestions,
    checkoutSuggestions, setCheckoutSuggestions, trackUpsell,
  } = usePublicMenuCart({ smartPricing, restaurantId, API, restaurant, categories, authToken: "" });

  const { pixelReady } = useAnalytics(restaurant);
  const restaurantStatus = computeRestaurantStatus(restaurant);
  const isOpen = restaurantStatus !== 'closed';
  const schedulingMode = restaurant?.scheduling_config?.mode || "allow";
  // The NFC/attendant payment setting must never change the public QR flow.
  const isPayAtEndMode = tablePayAtEnd;

  const tableSession = useTableSession({ tablePayAtEnd: isPayAtEndMode, tableId, restaurant, API, tableCurrentSessionId });
  // Direct product link (landing mode): resolve the product so the buffet
  // rules can be shown before starting an order for a flagged product.
  const landingProduct = landingModeActive && deepLinkSlug && products.length > 0
    ? findProductBySlug(products, deepLinkSlug)
    : null;

  useEffect(() => {
    if (!linkedCoupon || !landingProduct?.id || !restaurant?.id) {
      setLinkedOfferPreview(null);
      return;
    }
    const controller = new AbortController();
    fetch(`${API}/marketing/validate-coupon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        restaurant_id: restaurant.id,
        code: linkedCoupon,
        subtotal: Number(landingProduct.price || 0),
        items: [{ product_id: landingProduct.id, quantity: 1 }],
        order_type: "pickup",
        payment_method: "online",
      }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => setLinkedOfferPreview(data?.valid ? data : null))
      .catch(err => { if (err?.name !== "AbortError") setLinkedOfferPreview(null); });
    return () => controller.abort();
  }, [linkedCoupon, landingProduct?.id, landingProduct?.price, restaurant?.id]);
  const buffetRules = useBuffetRules({
    isTableOrder,
    tableSession: tableSession.tableSession,
    setTableSession: tableSession.setTableSession,
    tableId, API, cart, landingProduct,
  });
  const checkout = useCheckout({
    restaurant, restaurantId, isTableOrder, isOpen,
    schedulingMode, tablePayAtEnd, tablePaymentEnabled, paymentAtTableEnabled, tableNumber,
    tableSession: tableSession.tableSession, refreshTableSession: tableSession.refreshTableSession,
    cart, setCart, setCheckoutSuggestions, trackUpsell,
    API, paidOrderId, isPaid, cancelledOrderId, influencerCode,
    waPayReturn, waPayCancel, smartPricing, categories,
    fbclid, fbc, fbp, utm_source, utm_medium, utm_campaign, utm_content, utm_term,
    authToken: "",
  });
  const customerSession = useCustomerSession({
    restaurantId, searchParams,
    setCustomerPhone: checkout.setCustomerPhone,
    setCustomerName: checkout.setCustomerName,
    setCart, API,
  });

  useEffect(() => {
    if (tableSession.tableSession?.customer_name) checkout.setCustomerName(tableSession.tableSession.customer_name);
    if (tableSession.tableSession?.customer_phone) checkout.setCustomerPhone(tableSession.tableSession.customer_phone);
  }, [tableSession.tableSession]); // eslint-disable-line

  usePWAServiceWorker(restaurantId);
  usePWAManifest(restaurantId);
  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  usePublicMenuEffects({
    restaurant, restaurantId, theme, BACKEND_BASE,
    checkout, cartToken, deepLinkSlug, loading, products, combos,
    pixelReady, cartTotal, isTableOrder, tableNumber,
    API, navigate, setCart, searchParams,
    setShowReservation: checkout.setShowReservation,
    setReservationConfirmed: checkout.setReservationConfirmed,
  });

  if (loading) return <BrandedMenuLoader />;

  return (
    <PublicMenuLayout
      restaurant={restaurant} restaurantId={restaurantId}
      isSuspended={isSuspended} suspendedMessage={suspendedMessage}
      theme={theme} BACKEND_BASE={BACKEND_BASE} products={products}
      categories={categories} combos={combos}
      isTableOrder={isTableOrder} restaurantStatus={restaurantStatus}
      smartPricing={smartPricing} schedulingMode={schedulingMode}
      deepLinkSlug={deepLinkSlug} isRtl={isRtl}
      landingModeActive={landingModeActive} setLandingModeActive={setLandingModeActive}
      linkedOfferPreview={linkedOfferPreview}
      isPayAtEndMode={isPayAtEndMode} tableNumber={tableNumber} tableName={tableName} tableId={tableId}
      tablePayAtEnd={tablePayAtEnd} tablePaymentEnabled={tablePaymentEnabled}
      paymentAtTableEnabled={paymentAtTableEnabled} fetchMenu={fetchMenu}
      cart={cart} setCart={setCart} cartTotal={cartTotal} cartCount={cartCount}
      addToCart={addToCart} addToCartSilent={addToCartSilent}
      addSuggestionToCart={addSuggestionToCart} updateQuantity={updateQuantity}
      suggestions={suggestions} trackUpsell={trackUpsell}
      showSuggestions={showSuggestions} setShowSuggestions={setShowSuggestions}
      checkoutSuggestions={checkoutSuggestions} setCheckoutSuggestions={setCheckoutSuggestions}
      checkout={checkout} customerSession={customerSession} tableSession={tableSession}
      buffetRules={buffetRules}
      API={API}
    />
  );
}
